import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { __ } from "services/Translator";
import moment from "moment";
import api from "services/api";
import moneyFormatter from "services/moneyFormatter";

const WINNERS_TICK_MS = 3200;
const WINNERS_ANIM_MS = 550;

export default function Winners(props) {
  const [winnerTab, setWinnerTab] = useState("recent");
  const [feedPhase, setFeedPhase] = useState("idle");
  const maxRecentWinnersLength = 50;


  // Live feeds are backed by real transactions only. We never seed the UI with
  // fabricated winners; an empty feed hides the whole section (see render).
  const [recentWinners, setRecentWinners] = useState([]);
  const [topWinners, setTopWinners] = useState([]);

  // Read the current list via a ref so the fetch is a plain side effect —
  // never triggered from inside a setState updater (StrictMode double-invokes
  // those). Merging dedupes on tr_id so overlapping polls can't append the
  // same transaction twice.
  const recentWinnersRef = useRef(recentWinners);
  useEffect(() => {
    recentWinnersRef.current = recentWinners;
  }, [recentWinners]);

  const getRecentWinners = () => {
    const settled = recentWinnersRef.current.filter(
      (row) => !row._slideIncoming
    );
    const maxId = settled.length
      ? Math.max(...settled.map((item) => item.tr_id))
      : null;
    api
      .get("/games/recent-bets", { limit: 9, newerThan: maxId })
      .then((response) => {
        const rows = response?.data?.data ?? [];
        if (!rows.length) return;
        setRecentWinners((current) => {
          const seen = new Set(current.map((row) => String(row.tr_id)));
          const fresh = rows.filter((row) => !seen.has(String(row.tr_id)));
          if (!fresh.length) return current;
          const merged = current.concat(fresh);
          return merged.length > maxRecentWinnersLength
            ? merged.slice(merged.length - maxRecentWinnersLength)
            : merged;
        });
      })
      .catch(() => {});
  };

  const getTopWinners = () => {
    api
      .get("/games/top-winners", { limit: 9 })
      .then((response) => {
        const rows = response?.data?.data ?? [];
        if (rows.length) {
          setTopWinners(rows);
        }
      })
      .catch(() => {});
  };

  const checkIframe = () => {
    try {
      if (
        window.parent &&
        window.parent !== window &&
        window.parent.location.href !== window.location.href
      ) {
        window.parent.location.href = window.location.href;
      }
    } catch (_err) {
      // Cross-origin iframe: cannot sync parent location (expected in embedded previews).
    }
  };

  useEffect(() => {
    getTopWinners();
    getRecentWinners();
    checkIframe();
  }, []);

  const prepareSlideList = useCallback((prev) => {
    if (prev.length < 2) {
      return prev;
    }
    const last = prev[prev.length - 1];
    return [{ ...last, _slideIncoming: true }, ...prev];
  }, []);

  const finalizeSlideList = useCallback((prev) => {
    if (!prev.some((row) => row._slideIncoming)) {
      if (prev.length < 2) {
        return prev;
      }
      const next = [...prev];
      next.unshift(next.pop());
      return next;
    }
    return prev.slice(0, -1).map((row, index) => {
      if (index === 0) {
        const { _slideIncoming, ...rest } = row;
        return rest;
      }
      return row;
    });
  }, []);

  const getRowKey = (row) => (row._slideIncoming ? `incoming-${row.tr_id}` : String(row.tr_id));

  useEffect(() => {
    if (winnerTab !== "recent") {
      setFeedPhase("idle");
      return undefined;
    }

    let cancelled = false;
    const timers = [];
    let apiRound = 0;

    const schedule = (fn, delay) => {
      const id = setTimeout(fn, delay);
      timers.push(id);
    };

    const runCycle = () => {
      if (cancelled) {
        return;
      }

      setRecentWinners(prepareSlideList);
      setFeedPhase("prep");

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!cancelled) {
            setFeedPhase("slide");
          }
        });
      });

      schedule(() => {
        if (cancelled) {
          return;
        }

        setRecentWinners(finalizeSlideList);
        setFeedPhase("idle");

        apiRound = (apiRound + 1) % 5;
        if (apiRound === 0) {
          getRecentWinners();
        }

        schedule(runCycle, WINNERS_TICK_MS);
      }, WINNERS_ANIM_MS);
    };

    schedule(runCycle, WINNERS_TICK_MS);

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [winnerTab, prepareSlideList, finalizeSlideList]);

  const isMobile = Boolean(props.isMobileDevice);
  const hasRecent = recentWinners.length > 0;
  const hasTop = topWinners.length > 0;

  // Keep the active tab pointed at a tab that actually has data.
  useEffect(() => {
    if (winnerTab === "recent" && !hasRecent && hasTop) {
      setWinnerTab("top");
    } else if (winnerTab === "top" && !hasTop && hasRecent) {
      setWinnerTab("recent");
    }
  }, [winnerTab, hasRecent, hasTop]);

  // Never render an empty "Live activity" shell — hide until real data arrives.
  if (!hasRecent && !hasTop) {
    return null;
  }

  return (
    <section
      className={"winner_section_v2" + (isMobile ? " winner_section_v2--mobile" : "")}
      aria-label={__("Recent bets and winners")}
    >
      <header className="winners-section__header">
        <h2 className="winners-section__title">{__("Live activity")}</h2>
        <p className="winners-section__subtitle">{__("Recent bets across the casino")}</p>
      </header>

      <div className="winner_body">
        <div className="winners-tabs" role="tablist" aria-label={__("Activity view")}>
          {hasRecent && (
            <button
              type="button"
              role="tab"
              aria-selected={winnerTab === "recent"}
              className={"winners-tab" + (winnerTab === "recent" ? " winners-tab--active" : "")}
              onClick={() => setWinnerTab("recent")}
            >
              {__("Recent Bets")}
            </button>
          )}
          {hasTop && (
            <button
              type="button"
              role="tab"
              aria-selected={winnerTab === "top"}
              className={"winners-tab" + (winnerTab === "top" ? " winners-tab--active" : "")}
              onClick={() => setWinnerTab("top")}
            >
              {__("Big Winners")}
            </button>
          )}
        </div>

        <div className="winners-table">
          <div className="dataset_heading_area winners-table__head">
            <div className="dataset winners-table__row winners-table__row--head">
              {!isMobile && <div className="item col-game">{__("Game")}</div>}
              <div className="item col-user">{__("User")}</div>
              <div className="item col-time">{__("Time")}</div>
              {!isMobile && <div className="item col-wager">{__("Wager")}</div>}
              <div className="item col-mult winners-table__col--num">{__("Multiplier")}</div>
              <div className="item col-payout winners-table__col--num">{__("Payout")}</div>
            </div>
          </div>

        <div
          className={
            "dataset_content_area winners-feed animation animation--shadow " +
            (feedPhase !== "idle" ? " winners-feed--" + feedPhase : "") +
            (winnerTab === "recent" ? "" : " d-none")
          }
        >
          <div className="winners-feed__track">
            {recentWinners.length > 0 &&
              recentWinners.map((value) => (
                  <div className="dataset winners-table__row" key={getRowKey(value)}>
                    {!props.isMobileDevice && (
                      <>
                        {props.isAuthenticated && (
                          <Link to={"/play/" + value.game.slug} className="item gameName">
                            <span className="icon">
                              <img alt="" src={value.game.game_snapshot_url} />
                            </span>{" "}
                            <span className="text">{value.game.game_name}</span>
                          </Link>
                        )}

                        {!props.isAuthenticated && (
                          <button
                            type="button"
                            onClick={() => {
                              props.setModalSignUpPropsData(true);
                              setTimeout(function () {
                                props.setModalSignUpPropsData(false);
                              }, 500);
                            }}
                            className="item gameName"
                          >
                            <span className="icon">
                              <img alt="" src={value.game.game_snapshot_url} />
                            </span>{" "}
                            <span className="text">{value.game.game_name}</span>
                          </button>
                        )}
                      </>
                    )}

                    <div className="item playerName col-user">
                      {" "}
                      {value.player?.player_nickname ? (
                        value.player?.player_nickname
                      ) : (
                        <>
                          <svg
                            version="1.1"
                            x="0px"
                            y="0px"
                            viewBox="0 0 100 125"
                            enable-background="new 0 0 100 100"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <g>
                              <path d="M48.1,52.1c17.4,0,37.5-4,39.3-7.6S65,36.1,65,36.1c-5.8,2.7-17,2.7-17,2.7s-11.2,0-17-2.7c0,0-24.1,4.9-22.3,8.5   C10.6,48.1,30.2,52.1,48.1,52.1z" />
                              <path d="M63.1,29.2c0,0-3.4-19.4-5.9-23.2c-2.5-3.8-6.3,4.5-10.2,4.5c-3.9,0-4.8-5.3-8.3-5.5c-3.6-0.2-7.1,24.2-7.1,24.2   S50,33.4,63.1,29.2z" />
                              <path d="M31.5,53.3c0,0-1.6,7.4,8,7.6c0,0,3.7,0,5.6-3.3c0.6-1,1.5-1.6,2.7-1.6h0.4c1.1,0,2.1,0.6,2.7,1.6c1.9,3.3,5.6,3.3,5.6,3.3   c9.6-0.2,8-7.6,8-7.6c-4.4,1.5-11.3,1.4-13.7,1.3h0c-0.6,0-0.9,0-0.9,0c0,0,0,0,0,0l-3.8,0C46.2,54.5,36.9,55,31.5,53.3z" />
                              <path d="M27.7,53.3l-7.4-1.1l-12.8,11c0.3,0,0.8,0,1.2,0c5.7,0,20.8,4.8,20.8,4.8l-8,6.9c6,1.2,24.9,11.3,24.9,11.3l1.8-6.6   C33.1,68.5,27.7,53.3,27.7,53.3z" />
                              <path d="M92.6,62.9L77.1,52.1l-8.3,1.1c0.5,2.7-1.2,7.6-1.2,7.6C43.7,78.9,47.6,95,47.6,95C57.1,76.2,78,73.6,78,73.6l-9.5-4.5   C74.7,65.2,92.6,62.9,92.6,62.9z" />
                            </g>
                          </svg>
                          {__("Anonymous")}
                        </>
                      )}
                    </div>
                    <div className="item col-time">{moment(value.record_date, "YYYY-MM-DD HH:mm:ss").format("hh:mm A")}</div>
                    {!props.isMobileDevice && (
                      <div className="item col-wager winners-table__col--num">
                        <span className="icon">{moneyFormatter.currencyIcon("USD")}</span>
                        <span className="text">{value.bet_amount}</span>
                      </div>
                    )}
                    <div className="item mult col-mult winners-table__col--num">
                      <span className={"text " + (value.win_amount_float > 0 ? "green" : "")}>
                        {value.win_amount_float > 0
                          ? (value.win_amount_float / value.bet_amount_float).toFixed(2) + "X"
                          : "0.00X"}
                      </span>
                    </div>
                    <div className="item payout col-payout winners-table__col--num">
                      {value.win_amount_float > 0 ? (
                        <span className="icon">{moneyFormatter.currencyIcon("USD")}</span>
                      ) : (
                        <span className="icon">{moneyFormatter.currencyIcon("USDGRAY")}</span>
                      )}
                      <span className={"text " + (value.win_amount_float > 0 ? "green" : "")}>{value.win_amount}</span>
                    </div>
                  </div>
              ))}
          </div>
        </div>

        <div className={"dataset_content_area " + (winnerTab === "top" ? "" : " d-none")}>
          <div className="wrap">
            {topWinners.length > 0 &&
              topWinners.map((value) => (
                <div className="dataset winners-table__row" key={value.tr_id}>
                  {!props.isMobileDevice && (
                    <>
                      {props.isAuthenticated && (
                        <Link to={"/play/" + value.game.slug} className="item gameName">
                          <span className="icon">
                            <img alt="" src={value.game.game_snapshot_url} />
                          </span>{" "}
                          <span className="text">{value.game.game_name}</span>
                        </Link>
                      )}

                      {!props.isAuthenticated && (
                        <button
                          type="button"
                          onClick={() => {
                            props.setModalSignUpPropsData(true);
                            setTimeout(function () {
                              props.setModalSignUpPropsData(false);
                            }, 500);
                          }}
                          className="item gameName"
                        >
                          <span className="icon">
                            <img alt="" src={value.game.game_snapshot_url} />
                          </span>{" "}
                          <span className="text">{value.game.game_name}</span>
                        </button>
                      )}
                    </>
                  )}

                  <div className="item playerName col-user">
                    {value.player?.player_nickname ? (
                      value.player?.player_nickname
                    ) : (
                      <>
                        <svg
                          version="1.1"
                          x="0px"
                          y="0px"
                          viewBox="0 0 100 125"
                          enable-background="new 0 0 100 100"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <g>
                            <path d="M48.1,52.1c17.4,0,37.5-4,39.3-7.6S65,36.1,65,36.1c-5.8,2.7-17,2.7-17,2.7s-11.2,0-17-2.7c0,0-24.1,4.9-22.3,8.5   C10.6,48.1,30.2,52.1,48.1,52.1z" />
                            <path d="M63.1,29.2c0,0-3.4-19.4-5.9-23.2c-2.5-3.8-6.3,4.5-10.2,4.5c-3.9,0-4.8-5.3-8.3-5.5c-3.6-0.2-7.1,24.2-7.1,24.2   S50,33.4,63.1,29.2z" />
                            <path d="M31.5,53.3c0,0-1.6,7.4,8,7.6c0,0,3.7,0,5.6-3.3c0.6-1,1.5-1.6,2.7-1.6h0.4c1.1,0,2.1,0.6,2.7,1.6c1.9,3.3,5.6,3.3,5.6,3.3   c9.6-0.2,8-7.6,8-7.6c-4.4,1.5-11.3,1.4-13.7,1.3h0c-0.6,0-0.9,0-0.9,0c0,0,0,0,0,0l-3.8,0C46.2,54.5,36.9,55,31.5,53.3z" />
                            <path d="M27.7,53.3l-7.4-1.1l-12.8,11c0.3,0,0.8,0,1.2,0c5.7,0,20.8,4.8,20.8,4.8l-8,6.9c6,1.2,24.9,11.3,24.9,11.3l1.8-6.6   C33.1,68.5,27.7,53.3,27.7,53.3z" />
                            <path d="M92.6,62.9L77.1,52.1l-8.3,1.1c0.5,2.7-1.2,7.6-1.2,7.6C43.7,78.9,47.6,95,47.6,95C57.1,76.2,78,73.6,78,73.6l-9.5-4.5   C74.7,65.2,92.6,62.9,92.6,62.9z" />
                          </g>
                        </svg>
                        {__("Anonymous")}
                      </>
                    )}
                  </div>
                  <div className="item col-time">{moment(value.record_date, "YYYY-MM-DD HH:mm:ss").format("hh:mm A")}</div>
                  {!props.isMobileDevice && (
                    <div className="item col-wager winners-table__col--num">
                      <span className="icon">{moneyFormatter.currencyIcon("USD")}</span>
                      <span className="text">{value.bet_amount}</span>
                    </div>
                  )}
                  <div className="item mult col-mult winners-table__col--num">
                    <span className={"text " + (value.win_amount_float > 0 ? "green" : "")}>
                      {value.win_amount_float > 0
                        ? (value.win_amount_float / value.bet_amount_float).toFixed(2) + "X"
                        : "0.00X"}
                    </span>
                  </div>
                  <div className="item payout col-payout winners-table__col--num">
                    {value.win_amount_float > 0 ? (
                      <span className="icon">{moneyFormatter.currencyIcon("USD")}</span>
                    ) : (
                      <span className="icon">{moneyFormatter.currencyIcon("USDGRAY")}</span>
                    )}
                    <span className={"text " + (value.win_amount_float > 0 ? "green" : "")}>{value.win_amount}</span>
                  </div>
                </div>
              ))}
          </div>
        </div>
        </div>
      </div>
    </section>
  );
}
